import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import { VEHICLE_CLASSES } from "@/lib/vehicleClasses";

/**
 * Best-for copy is added here (visual only, not persisted). Keeps
 * VEHICLE_CLASSES as the single source of vehicle data.
 */
const VEHICLE_META = {
  executive_sedan: {
    bestFor: "Airport transfers · Corporate mornings · Everyday executive travel",
    accent: "The Everyday Standard",
  },
  first_class_sedan: {
    bestFor: "VIP guests · Hotel arrivals · Occasions that require our finest",
    accent: "The Flagship",
  },
  luxury_suv: {
    bestFor: "Families · Small groups · Longer-distance NSW travel",
    accent: "Elevated Presence",
  },
  executive_van: {
    bestFor: "Small parties · Roadshows · Airport groups with luggage",
    accent: "Composed Group Travel",
  },
  luxury_van: {
    bestFor: "Large parties · Weddings · Corporate transfers of ten or more",
    accent: "Capacity, Uncompromised",
  },
};

export default function FleetPage() {
  const navigate = useNavigate();

  const goToBook = (className) => {
    const params = new URLSearchParams({ vehicle: className, mode: "booking" });
    navigate(`/book?${params.toString()}`);
  };

  return (
    <div data-testid="fleet-page" className="bg-near-black">
      <Navbar transparent />

      {/* -------- Cinematic Collection Hero -------- */}
      <FleetHero />

      {/* -------- Collection Index (jump list) -------- */}
      <CollectionIndex />

      {/* -------- One editorial section per vehicle -------- */}
      {VEHICLE_CLASSES.map((v, i) => (
        <VehicleShowcase
          key={v.id}
          vehicle={v}
          meta={VEHICLE_META[v.id]}
          index={i}
          total={VEHICLE_CLASSES.length}
          onBook={() => goToBook(v.name)}
        />
      ))}

      {/* -------- Closing invitation -------- */}
      <FleetClosing onBook={() => goToBook("Executive Sedan")} />

      <Footer />
      <FloatingActions />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero — cinematic collection cover                                  */
/* ------------------------------------------------------------------ */
function FleetHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      ref={ref}
      data-testid="fleet-hero-section"
      className="relative h-[100svh] overflow-hidden bg-near-black"
    >
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/3aeb2b71-c3a3-4775-b2b3-77824c8c9518/images/8ec3520a33cc28140e711c6988ab40f8a3fe0239b144f12ac99de9ad643c8b3b.jpeg"
          alt="Luxury vehicle interior detail"
          className="w-full h-[120%] object-cover sjc-slow-reveal"
          loading="eager"
        />
      </motion.div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(3,5,7,0.75) 0%, rgba(3,5,7,0.45) 40%, rgba(3,5,7,0.85) 100%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(90deg, rgba(3,5,7,0.6) 0%, transparent 60%)" }}
      />

      <div className="relative z-10 h-full max-w-[92rem] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="sjc-eyebrow text-warm-white/70 flex items-center gap-4"
        >
          <span className="sjc-hairline w-10" />
          The Sydney James Collection
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-display mt-8 text-warm-white text-5xl sm:text-6xl md:text-7xl lg:text-[104px] leading-[1] tracking-[-0.02em] font-light max-w-5xl"
        >
          Five classes,
          <br />
          <span className="italic">one unwavering standard.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-warm-white/65 text-base md:text-lg font-light leading-relaxed"
        >
          Every vehicle in our care is chosen, presented and driven to the same
          standard — regardless of the journey ahead.
        </motion.p>
      </div>

      <div className="absolute bottom-6 right-8 md:right-12 z-10 flex items-center gap-3 text-warm-white/60">
        <span className="sjc-eyebrow">Scroll</span>
        <span className="w-16 h-px bg-gold/50" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Collection Index — restrained typographic jump list                */
/* ------------------------------------------------------------------ */
function CollectionIndex() {
  const scrollTo = (id) => {
    document.getElementById(`class-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="fleet-index-section"
      className="bg-warm-white border-t border-charcoal/10 py-16 md:py-24"
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-4">
            <p className="sjc-eyebrow text-muted-light flex items-center gap-3">
              <span className="sjc-hairline w-6" />
              The Index
            </p>
            <h2 className="font-display mt-4 text-3xl md:text-4xl text-charcoal leading-tight tracking-tight">
              Choose by capacity,
              <br />
              <span className="italic font-light">or by occasion.</span>
            </h2>
            <p
              data-testid="fleet-imagery-note"
              className="mt-6 text-muted-light/80 italic font-light leading-relaxed max-w-xs"
              style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "15px" }}
            >
              Imagery is representative of each vehicle class — the exact vehicle
              assigned may vary.
            </p>
          </div>
          <ol className="md:col-span-8 space-y-0 border-t border-charcoal/10">
            {VEHICLE_CLASSES.map((v, i) => (
              <li
                key={v.id}
                className="border-b border-charcoal/10"
              >
                <button
                  type="button"
                  data-testid={`fleet-index-${v.id}`}
                  onClick={() => scrollTo(v.id)}
                  className="group w-full py-6 md:py-7 flex items-baseline gap-6 md:gap-10 text-left transition-colors duration-500 hover:text-gold"
                >
                  <span className="font-display text-gold/70 italic text-lg w-8">
                    0{i + 1}
                  </span>
                  <span className="flex-1 font-display text-2xl md:text-[32px] leading-tight tracking-tight text-charcoal group-hover:text-gold transition-colors duration-500">
                    {v.name}
                  </span>
                  <span className="hidden md:inline-block text-[11px] tracking-[0.28em] uppercase text-muted-light font-medium">
                    {v.capacity.passengers}
                  </span>
                  <span className="w-6 h-px bg-charcoal/15 group-hover:w-14 group-hover:bg-gold transition-all duration-500" />
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Vehicle Showcase — full editorial section per vehicle              */
/*  Alternates image left/right, alternates dark / ivory backgrounds  */
/* ------------------------------------------------------------------ */
function VehicleShowcase({ vehicle, meta, index, total, onBook }) {
  // Layout variant chosen by index — creates the "varied composition" feel.
  //  0: warm ivory studio, image right
  //  1: dark full-bleed (flagship)
  //  2: soft stone, image left
  //  3: ivory, image right
  //  4: dark full-bleed close
  const variant =
    index === 1 ? "full-bleed" : index === 4 ? "full-bleed-close" : index === 2 ? "stone" : "ivory";
  const imageRight = index % 2 === 0;

  if (variant === "full-bleed" || variant === "full-bleed-close") {
    return (
      <FullBleedShowcase
        vehicle={vehicle}
        meta={meta}
        index={index}
        total={total}
        onBook={onBook}
        isFlagship={variant === "full-bleed"}
      />
    );
  }

  return (
    <section
      id={`class-${vehicle.id}`}
      data-testid={`fleet-class-${vehicle.id}`}
      className={`relative py-24 md:py-32 border-t border-charcoal/10 ${
        variant === "stone" ? "bg-stone" : "bg-ivory"
      }`}
    >
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className={`lg:col-span-7 ${imageRight ? "lg:order-2" : "lg:order-1"}`}
        >
          <div className="relative overflow-hidden bg-stone">
            <img
              src={vehicle.image}
              alt={vehicle.name}
              data-testid={`fleet-image-${vehicle.id}`}
              className="w-full h-[420px] md:h-[560px] lg:h-[640px] object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement.classList.add(
                  "bg-gradient-to-br",
                  "from-stone",
                  "to-warm-white",
                );
              }}
            />
          </div>
        </motion.div>

        {/* Copy */}
        <div className={`lg:col-span-5 ${imageRight ? "lg:order-1" : "lg:order-2"}`}>
          <VehicleCopy
            vehicle={vehicle}
            meta={meta}
            index={index}
            total={total}
            onBook={onBook}
            isIvory
          />
        </div>
      </div>
    </section>
  );
}

/* Flagship / cinematic full-bleed showcase */
function FullBleedShowcase({ vehicle, meta, index, total, onBook, isFlagship }) {
  return (
    <section
      id={`class-${vehicle.id}`}
      data-testid={`fleet-class-${vehicle.id}`}
      className="relative bg-near-black border-t border-white/10"
    >
      {/* Cinematic image */}
      <div className="relative h-[70vh] md:h-[90vh] overflow-hidden">
        <img
          src={vehicle.image}
          alt={vehicle.name}
          data-testid={`fleet-image-${vehicle.id}`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement.classList.add(
              "bg-gradient-to-br",
              "from-surface-dark",
              "to-near-black",
            );
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(3,5,7,0.35) 0%, rgba(3,5,7,0.15) 40%, rgba(3,5,7,0.9) 100%)",
          }}
        />

        <div className="absolute inset-0 flex flex-col justify-between max-w-[92rem] mx-auto px-6 lg:px-12 py-16 md:py-24">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="sjc-eyebrow text-warm-white/70 flex items-center gap-4"
          >
            <span className="sjc-hairline w-10" />
            {isFlagship ? "The Flagship" : `0${index + 1} · Collection Close`}
          </motion.p>

          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-gold/80 italic text-lg md:text-xl mb-4"
            >
              0{index + 1} / 0{total}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-warm-white text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[1] tracking-[-0.02em]"
            >
              {vehicle.name.split(" ").map((word, i, arr) =>
                i === arr.length - 1 ? (
                  <span key={i} className="italic font-light">
                    {word}
                  </span>
                ) : (
                  <span key={i}>{word} </span>
                ),
              )}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 text-warm-white/70 text-base md:text-lg font-light max-w-xl leading-relaxed"
            >
              {vehicle.description}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Specifications strip below flagship image */}
      <div className="max-w-[92rem] mx-auto px-6 lg:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="sjc-eyebrow text-gold">{meta?.accent}</p>
            <p
              className="mt-6 font-display text-2xl md:text-[28px] leading-[1.3] text-warm-white italic"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              &ldquo;{vehicle.tagline}&rdquo;
            </p>
          </div>
          <div className="lg:col-span-8">
            <SpecificationTable vehicle={vehicle} meta={meta} onBook={onBook} dark />
          </div>
        </div>
      </div>
    </section>
  );
}

/* Copy block used by the alternating editorial variants */
function VehicleCopy({ vehicle, meta, index, total, onBook, isIvory }) {
  const textColor = isIvory ? "text-charcoal" : "text-warm-white";
  const softColor = isIvory ? "text-muted-light" : "text-warm-white/60";
  const borderColor = isIvory ? "border-charcoal/10" : "border-white/10";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="flex items-baseline gap-4">
        <span className="font-display text-gold/80 italic text-base">
          0{index + 1} / 0{total}
        </span>
        <span className={`sjc-eyebrow ${isIvory ? "text-muted-light" : "text-warm-white/50"}`}>
          {meta?.accent}
        </span>
      </div>

      <h2
        className={`font-display mt-6 text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-[-0.015em] ${textColor}`}
      >
        {vehicle.name.split(" ").map((word, i, arr) =>
          i === arr.length - 1 ? (
            <span key={i} className="italic font-light">
              {word}
            </span>
          ) : (
            <span key={i}>{word} </span>
          ),
        )}
      </h2>

      <p
        className={`mt-4 font-display italic text-lg md:text-xl ${
          isIvory ? "text-muted-light" : "text-gold/85"
        }`}
      >
        {vehicle.tagline}
      </p>

      <p className={`mt-6 text-[15px] md:text-base leading-[1.75] font-light max-w-lg ${softColor}`}>
        {vehicle.description}
      </p>

      <div className={`mt-10 pt-8 border-t ${borderColor}`}>
        <SpecificationTable vehicle={vehicle} meta={meta} onBook={onBook} dark={!isIvory} />
      </div>
    </motion.div>
  );
}

/* Sophisticated specification table — hairline rows, not icon cards */
function SpecificationTable({ vehicle, meta, onBook, dark = true }) {
  const label = dark ? "text-warm-white/60" : "text-muted-light/80";
  const value = dark ? "text-warm-white" : "text-charcoal";
  const border = dark ? "border-white/10" : "border-charcoal/10";

  const rows = [
    { k: "Passengers", v: vehicle.capacity.passengers },
    { k: "Luggage", v: vehicle.capacity.luggage },
    { k: "Comfort", v: vehicle.capacity.comfort },
    { k: "Representative models", v: (vehicle.models || []).join(" · ") },
    ...(meta?.bestFor ? [{ k: "Best for", v: meta.bestFor }] : []),
  ];

  return (
    <div>
      <p className="sjc-eyebrow text-gold">Specifications</p>

      <dl className={`mt-6 border-t ${border}`}>
        {rows.map((r) => (
          <div
            key={r.k}
            className={`grid grid-cols-[130px_1fr] md:grid-cols-[180px_1fr] gap-6 py-4 border-b ${border}`}
          >
            <dt className={`sjc-eyebrow ${label} self-center`}>{r.k}</dt>
            <dd className={`text-sm md:text-[15px] leading-relaxed font-light ${value}`}>
              {r.v}
            </dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        data-testid={`fleet-book-${vehicle.id}`}
        onClick={onBook}
        className={`group mt-10 inline-flex items-center gap-4 px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 ${
          dark
            ? "bg-warm-white text-midnight hover:bg-gold"
            : "bg-midnight text-warm-white hover:bg-gold hover:text-midnight"
        }`}
      >
        <span>Reserve this class</span>
        <span
          className={`w-2 h-2 rounded-full transition-transform duration-500 group-hover:translate-x-1 ${
            dark ? "bg-midnight" : "bg-warm-white group-hover:bg-midnight"
          }`}
        />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Closing invitation                                                 */
/* ------------------------------------------------------------------ */
function FleetClosing({ onBook }) {
  return (
    <section
      data-testid="fleet-closing-section"
      className="bg-ivory py-24 md:py-36 border-t border-charcoal/10"
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
        <p className="sjc-eyebrow text-muted-light flex items-center justify-center gap-4">
          <span className="sjc-hairline w-8" />
          Undecided?
          <span className="sjc-hairline w-8" />
        </p>
        <h2 className="font-display mt-8 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.015em] text-charcoal">
          Let us recommend the
          <br />
          <span className="italic font-light">right vehicle for the journey.</span>
        </h2>
        <p className="mt-8 text-muted-light text-base md:text-lg leading-[1.75] font-light">
          Tell us where and when — the booking will recommend the class that best
          suits your group and luggage automatically. You may adjust it at any time.
        </p>
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8">
          <button
            type="button"
            data-testid="fleet-closing-cta"
            onClick={onBook}
            className="group inline-flex items-center gap-3 bg-midnight text-warm-white px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 hover:bg-gold hover:text-midnight"
          >
            <span>Begin Your Booking</span>
            <span className="w-2 h-2 rounded-full bg-warm-white group-hover:bg-midnight transition-transform duration-500 group-hover:translate-x-1" />
          </button>
          <a
            href="tel:+61400000000"
            data-testid="fleet-closing-phone"
            className="group flex items-center gap-4 text-charcoal/85 hover:text-charcoal transition-colors duration-300"
          >
            <span className="w-8 h-px bg-gold transition-all duration-500 group-hover:w-12" />
            <div className="text-left">
              <p className="sjc-eyebrow text-muted-light/70">Or speak with us</p>
              <p className="mt-1 font-display text-xl md:text-2xl tracking-tight">+61 400 000 000</p>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
